import Link from "next/link";
import { getGlobalData } from "utils/api";
import { CMSUrls } from "utils/cmsHelper";
import { ContactBox } from "utils/constants";
import { getLocalizedPaths } from "utils/localize";
import Image from "next/image";

import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function Accessibility({ metadata }) {
  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-12 lg:gap-spacing-14 text-sm lg:text-base">
        <Breadcrumb />
        <div className="4xl:text-xl">
          <p className="text-left">
            We are committed to ensuring that you are satisfied with the
            products and services we provide and aim to respond to enquiries in
            a timely and professional manner.
          </p>
          <p className="text-left mt-6">
            If you wish to make a complaint, our commitment is to resolve the
            matter as soon as possible.
          </p>
          <p className="text-left">
            So we can fully understand what&apos;s gone wrong and to get the
            best possible resolution for you, there are several ways to get in
            touch.
          </p>
        </div>
        <ContactBox email="complaints@onmo.app" />
        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/page.svg"
            alt="page-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Once you&apos;ve made a complaint
            </h3>
            <p>
              Onmo aims to acknowledge and resolve your complaint as quickly as
              possible.
            </p>
            <div className="mt-6">
              <ol className="list-decimal pl-6 space-y-6">
                <li>
                  Once we have received your complaint, we&apos;ll contact you
                  within 5 working days. This is to acknowledge receipt of your
                  complaint. Where possible we will also try to resolve your
                  complaint within this timeframe.
                </li>
                <li>
                  We will investigate your complaint. If we are unable to
                  resolve your complaint by the end of the third business day,
                  we will continue our investigations. During our investigation
                  we may reach out to you as applicable to ask you for further
                  details or supporting evidence. In the unlikely event that
                  we’re unable to resolve your complaint, and we need more time
                  to resolve it, we will advise you of this and keep you
                  informed as best as we can.
                </li>
                <li>
                  We will inform you of the outcome of your complaint. We will
                  take the appropriate steps to address your complaint and
                  inform you of the outcome in writing.
                </li>
              </ol>
            </div>
            <p className="mt-6">
              Please note that depending on your complaint type we have up to 8
              weeks to investigate your complaint and issue you with a
              &apos;final response&apos; which states the outcome of your
              complaint.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/dislike.svg"
            alt="dislike-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              What if I don&apos;t agree with the outcome?
            </h3>
            <p className="4xl:text-xl">
              In the unlikely event that you&apos;re dissatisfied with our final
              response or handling of your complaint, you have the right to
              refer your complaint to the Financial Ombudsman Service who
              resolve disputes between customers and financial services
              companies, free of charge. You must contact them within six months
              of receiving our final response.
            </p>
            <p className="mt-6 4xl:text-xl">
              You can find their details by visiting their website here:{" "}
              <Link
                href="https://www.financial-ombudsman.org.uk/"
                target="_blank"
              >
                https://www.financial-ombudsman.org.uk/
              </Link>
            </p>
            <p className="mt-6 4xl:text-xl">
              Other ways to get in touch with the Ombudsman:
            </p>
            <p className="mt-6 4xl:text-xl">
              <span className="block">
                Address: Financial Ombudsman Service, Exchange Tower, London,
                E14 9SR
              </span>
              <span className="block">
                Call centre: 0800 023 4567 or 0300 123 9123
              </span>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 4xl:text-xl text-sm">
          {/* TODO: change the links */}
          <Link
            href="https://www.financial-ombudsman.org.uk/businesses/resolving-complaint/ordering-leaflet/leaflet"
            className="bg-onmoPurple-50 rounded-xl text-onmoPrimary flex md:items-center px-8 py-5 gap-2"
            target="_blank"
          >
            <Image
              src="/images/icons/document-view.svg"
              alt="download-icon"
              className="h-4 w-5 m-1 self-center"
              width="20"
              height="16"
            />
            <p>Financial Ombudsman Services Standard Explanatory Leaflet</p>
          </Link>

          <Link
            href={CMSUrls.onmoComplaintUrl}
            className="bg-onmoPurple-50 rounded-xl text-onmoPrimary flex md:items-center px-8 py-5 gap-2"
            target="_blank"
          >
            <Image
              src="/images/icons/document-view.svg"
              alt="download-icon"
              className="h-4 w-5 m-1 self-center"
              height="16"
              width="20"
            />
            <p>Onmo complaints data table</p>
          </Link>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `How do I raise a complaint?`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
